import { useEffect, useRef } from 'react'

// Events that count as "user activity" and reset the idle timer.
const ACTIVITY_EVENTS = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click']

/**
 * Calls onIdle after `timeoutMs` milliseconds without user activity.
 * Pass `timeoutMs = null` (or 0) to disable � useful for "only run when logged in".
 *
 * Activity is throttled to once per second so mousemove doesn't recreate the timer
 * on every pixel of motion.
 */
export function useIdleTimeout(timeoutMs, onIdle) {
    const timerRef = useRef(null)
    const onIdleRef = useRef(onIdle)

    // Always use the latest callback without restarting the effect
    useEffect(() => {
        onIdleRef.current = onIdle
    }, [onIdle])

    useEffect(() => {
        if (!timeoutMs) return

        function resetTimer() {
            if (timerRef.current) clearTimeout(timerRef.current)
            timerRef.current = setTimeout(() => {
                onIdleRef.current?.()
            }, timeoutMs)
        }

        let throttle = null
        function handleActivity() {
            if (throttle) return
            throttle = setTimeout(() => { throttle = null }, 1000)
            resetTimer()
        }

        ACTIVITY_EVENTS.forEach(evt => {
            window.addEventListener(evt, handleActivity, { passive: true })
        })

        // Start the timer on mount
        resetTimer()

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
            if (throttle) clearTimeout(throttle)
            ACTIVITY_EVENTS.forEach(evt => {
                window.removeEventListener(evt, handleActivity)
            })
        }
    }, [timeoutMs])
}